import React, { useState } from "react";
import { formatNumber } from "../../../../../common/utils/helper";
import { useLanguage } from "../../../../../common/i18n/LanguageContext";

function Step2({
  isThaiTableVisible,
  rewardTypeGroups = [],
  minbetUpdate,
  setMinbetUpdate,
  minbetUpline = {},
  maxbetUpdate,
  setMaxbetUpdate,
  maxbetUpline = {},
  maxbetpermatchUpdate,
  setMaxbetpermatchUpdate,
  maxbetpermatchUpline = {},
  handleChangepayrateObj,
  uplineCurrencySymbol = "",
}) {
  const { t } = useLanguage();
  // Which group+field combos have "ใช้ค่าเดียวกันทั้งกลุ่ม" checked — keyed
  // by `${group.id}_${fieldKey}`. While checked, only the group's first
  // stake stays editable for that field; editing it cascades the value to
  // every other stake in the group live.
  const [syncedFields, setSyncedFields] = useState({});

  const fields = [
    {
      key: "minbet",
      label: t("commission_step_minbet_label"),
      value: minbetUpdate,
      setValue: setMinbetUpdate,
      upline: minbetUpline,
    },
    {
      key: "maxbet",
      label: t("commission_step_maxbet_label"),
      value: maxbetUpdate,
      setValue: setMaxbetUpdate,
      upline: maxbetUpline,
    },
    {
      key: "maxbetpermatch",
      label: t("commission_step_maxbetpermatch_label"),
      value: maxbetpermatchUpdate,
      setValue: setMaxbetpermatchUpdate,
      upline: maxbetpermatchUpline,
    },
  ];

  const isSynced = (groupId, fieldKey) =>
    !!syncedFields[`${groupId}_${fieldKey}`];

  const cascadeToGroup = (group, value, setValue) => {
    const rewardTypes = group.rewardTypes || [];
    setValue((prev) => {
      const next = { ...prev };
      rewardTypes.forEach((rt) => {
        next[rt.code] = value;
      });
      return next;
    });
  };

  const handleToggleFieldSync = (group, field, checked) => {
    setSyncedFields((prev) => ({
      ...prev,
      [`${group.id}_${field.key}`]: checked,
    }));
    if (!checked) return;
    const firstRt = (group.rewardTypes || [])[0];
    if (!firstRt) return;
    cascadeToGroup(group, field.value[firstRt.code] ?? 0, field.setValue);
  };

  const handleFieldChange = (e, group, rt, field) => {
    const isFirst = (group.rewardTypes || [])[0]?.id === rt.id;
    if (isSynced(group.id, field.key) && isFirst) {
      const newValue = Number(e.target.value) || 0;
      cascadeToGroup(group, newValue, field.setValue);
      return;
    }
    handleChangepayrateObj(e, field.value, field.setValue);
  };

  return (
    <div className="md:w-full h-auto flex p-2 flex-col space-y-2 bg-slate-200 rounded-lg">
      {rewardTypeGroups.length === 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm text-center text-slate-500">
          {t("member_commission_no_groups")}
        </div>
      )}

      {rewardTypeGroups.map((group) => {
        const firstRtId = (group.rewardTypes || [])[0]?.id;
        return (
          <div
            key={group.id}
            className="w-full text-center justify p-1 rounded-sm bg-blue-400 hover:bg-blue-800"
          >
            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="text-white text-[15px] font-bold">
                {group.name}
              </span>
              {fields.map((field) => (
                <label
                  key={field.key}
                  className="flex items-center gap-1 text-white text-[15px] font-bold"
                >
                  <input
                    type="checkbox"
                    checked={isSynced(group.id, field.key)}
                    onChange={(e) =>
                      handleToggleFieldSync(group, field, e.target.checked)
                    }
                  />
                  {field.label}
                </label>
              ))}
            </div>
            {isThaiTableVisible && (
              <div className="rounded overflow-hidden mt-1 bg-white p-2">
                {(group.rewardTypes || []).length === 0 && (
                  <span className="text-[12px] text-slate-400">
                    {t("commission_step_no_items")}
                  </span>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {(group.rewardTypes || []).map((rt) => {
                    const isFirst = rt.id === firstRtId;
                    return (
                      <div
                        key={rt.id}
                        className="border-[1px] border-slate-300 rounded-lg p-2"
                        title={`${rt.code} (${rt.stake}) — ${rt.exp}`}
                      >
                        <div className="font-bold text-[12px] text-center mb-1">
                          {rt.name}
                        </div>
                        <div className="flex flex-col gap-1">
                          {fields.map((field) => {
                            const fieldDisabled =
                              isSynced(group.id, field.key) && !isFirst;
                            return (
                              <div
                                key={field.key}
                                className="flex items-center gap-1"
                              >
                                <label className="text-[11px] text-slate-500 w-[70px] text-left shrink-0">
                                  {field.label}
                                </label>
                                <div className="flex flex-col w-full">
                                  {field.upline[rt.code] !== undefined && (
                                    <span className="font-bold text-amber-500 justify-items-center items-center text-[11px] text-center">
                                      {formatNumber(field.upline[rt.code])}{" "}
                                      {uplineCurrencySymbol}
                                    </span>
                                  )}
                                  <input
                                    type="number"
                                    min="0"
                                    disabled={fieldDisabled}
                                    className="w-full font-bold text-[12px] border-[1px] border-slate-300 rounded-lg text-center disabled:bg-slate-100 disabled:text-slate-400"
                                    name={rt.code}
                                    value={field.value[rt.code] ?? 0}
                                    onChange={(e) =>
                                      handleFieldChange(e, group, rt, field)
                                    }
                                    onClick={(e) => e.target.select()}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Step2;
